import { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  BottomNavigation,
  BottomNavigationAction,
  Container,
  CssBaseline,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import PersonIcon from "@mui/icons-material/Person";
import SportsBaseballIcon from "@mui/icons-material/SportsBaseball";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { Outlet, useNavigate } from "react-router-dom";
import useUserBase from "./hooks/useUserBase";

export default function UserBase({ size = "md" }) {
  const [value, setValue] = useState(0);
  const navigate = useNavigate();
  const {
    userLoggedData,
    userInfo,
    userRankings,
    noCompetitionMessage,
    initialize,
    refreshPlayerData,
  } = useUserBase();

  useEffect(() => {
    initialize();
  }, [initialize]);

  const userName = userLoggedData?.name || "Jogador";
  const greetingName = userName.split(" ")[0];
  const avatarSrc =
    userLoggedData?.player?.avatarUrl ||
    userLoggedData?.avatarUrl ||
    userInfo.avatarUrl ||
    "";

  const handleChange = (event, newValue) => {
    setValue(newValue);
    refreshPlayerData();
    switch (newValue) {
      case 0:
        navigate("/private/user/home");
        break;
      case 1:
        navigate("/private/user/matches");
        break;
      case 2:
        navigate("/private/user/ranking");
        break;
      case 3:
        navigate("/private/user/profile");
        break;
      default:
        break;
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100dvh" }}>
      <CssBaseline />

      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
        }}
      >
        <Container maxWidth={size} sx={{ pt: 2 }}>
          <Paper
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              p: 2,
              backgroundColor: "background.paper",
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                src={avatarSrc || undefined}
                alt={userName}
                sx={{
                  width: 60,
                  height: 60,
                  bgcolor: "action.selected",
                  color: "text.secondary",
                  fontWeight: 700,
                }}
              >
                {!avatarSrc ? greetingName?.[0]?.toUpperCase() || "U" : null}
              </Avatar>

              <Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, lineHeight: 1.2 }}
                >
                  Olá, {greetingName}!
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Bem vindo ao Apeak Club
                </Typography>
              </Box>
            </Stack>

            <Divider sx={{ my: 1.5 }} />

            {noCompetitionMessage ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 600, textAlign: "center" }}
              >
                {noCompetitionMessage}
              </Typography>
            ) : (
              <Stack spacing={1}>
                {userRankings.length > 0 ? (
                  userRankings.map((ranking) => (
                    <Stack
                      key={
                        ranking.id ??
                        `${ranking.name}-${ranking.year ?? "current"}`
                      }
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{
                        p: 1.25,
                        borderRadius: 1.5,
                        bgcolor: "action.hover",
                      }}
                    >
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 700 }}
                        >
                          {ranking.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {ranking.matchesPlayed} partidas • {ranking.wins}V/
                          {ranking.losses}D
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: "right" }}>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600 }}
                        >
                          Pontos: {ranking.score}
                        </Typography>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600 }}
                        >
                          Rank: #{ranking.rank ?? "-"}
                        </Typography>
                      </Box>
                    </Stack>
                  ))
                ) : (
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Pontos: {userInfo.score}
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Rank: {userInfo.rank}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            )}
          </Paper>
        </Container>

        <Container maxWidth={size} sx={{ py: 2 }}>
          <Outlet />
        </Container>
      </Box>

      <BottomNavigation
        value={value}
        onChange={handleChange}
        showLabels
        sx={{
          borderTop: "1px solid #ddd",
          position: "sticky",
          bottom: 0,
          zIndex: 1000,
          height: 72,
          "& .MuiBottomNavigationAction-root": {
            py: 1.5,
            minWidth: 0,
            "& .MuiBottomNavigationAction-label": {
              fontSize: "0.75rem",
              mt: 0.5,
            },
            "& .MuiSvgIcon-root": {
              fontSize: 26,
            },
          },
        }}
      >
        <BottomNavigationAction label="Home" icon={<HomeIcon />} />
        <BottomNavigationAction
          label="Partidas"
          icon={<SportsBaseballIcon />}
        />
        <BottomNavigationAction label="Ranking" icon={<LeaderboardIcon />} />
        <BottomNavigationAction label="Mais" icon={<MoreHorizIcon />} />
      </BottomNavigation>
    </Box>
  );
}
